import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BellRing, Mail, MessageCircle } from "lucide-react";
import { createReminder } from "@/lib/api";

export const AlertSignup = () => {
  const [channel, setChannel] = useState("email");
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload =
        channel === "email" ? { channel, email: contact } : { channel, phone: contact };
      const res = await createReminder(payload);
      if (res.status === "already") {
        toast.info("Je staat al op de lijst");
      } else {
        toast.success("Aangemeld!", {
          description: "Je krijgt een seintje zodra een nieuwe kaartverkoop live gaat.",
        });
      }
      setContact("");
    } catch {
      toast.error("Aanmelden mislukt. Probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24" data-testid="alerts-signup-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative overflow-hidden rounded-3xl border border-volt/20 bg-[#0D0D12] p-8 sm:p-12"
      >
        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-volt/10 blur-[100px]" />
        <div className="relative flex flex-col items-center text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">
            E-mail & WhatsApp alerts
          </p>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Altijd als eerste op de hoogte
          </h2>
          <p className="mt-3 max-w-xl text-slate-400">
            Laat je e-mailadres of WhatsApp-nummer achter en ontvang een seintje zodra een nieuwe
            kaartverkoop live gaat.
          </p>

          <div className="mt-7 inline-flex overflow-hidden rounded-full border border-white/15" role="tablist">
            <button
              type="button"
              onClick={() => setChannel("email")}
              data-testid="alert-channel-email"
              className={`flex items-center gap-1.5 px-5 py-2.5 text-xs font-bold transition-colors ${
                channel === "email" ? "bg-volt text-black" : "text-slate-400 hover:text-white"
              }`}
            >
              <Mail className="h-3.5 w-3.5" /> E-mail
            </button>
            <button
              type="button"
              onClick={() => setChannel("whatsapp")}
              data-testid="alert-channel-whatsapp"
              className={`flex items-center gap-1.5 px-5 py-2.5 text-xs font-bold transition-colors ${
                channel === "whatsapp" ? "bg-volt text-black" : "text-slate-400 hover:text-white"
              }`}
            >
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </button>
          </div>

          <form
            onSubmit={submit}
            data-testid="alert-signup-form"
            className="mt-4 flex w-full max-w-xl items-center gap-2 rounded-full border border-white/15 bg-black/50 p-1.5 transition-colors focus-within:border-volt/60"
          >
            {channel === "email" ? (
              <Mail className="ml-3 h-4 w-4 shrink-0 text-slate-500" />
            ) : (
              <MessageCircle className="ml-3 h-4 w-4 shrink-0 text-slate-500" />
            )}
            <input
              required
              type={channel === "email" ? "email" : "tel"}
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder={channel === "email" ? "jij@voorbeeld.nl" : "+31 6 12345678"}
              data-testid="alert-contact-input"
              className="w-full min-w-0 bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="alert-submit-button"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-volt px-5 py-2.5 text-sm font-bold text-black transition-transform hover:scale-105 hover:bg-volt-hover disabled:opacity-50"
            >
              <BellRing className="h-4 w-4" />
              <span className="hidden sm:inline">{loading ? "Bezig..." : "Houd me op de hoogte"}</span>
              <span className="sm:hidden">{loading ? "..." : "Verstuur"}</span>
            </button>
          </form>
          <p className="mt-3 text-[11px] text-slate-600">
            Geen spam — alleen een seintje bij een nieuwe drop. Uitschrijven kan altijd.
          </p>
        </div>
      </motion.div>
    </section>
  );
};
